import { useState } from "react";
import { DatabaseBackup, Download, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile } from "../api";
import { useAuth } from "../auth";

const REPORTS = [
    { kind: "sales", label: "Ticket sales report", file: "lakou-sales-report.csv" },
    { kind: "guests", label: "Guest list", file: "lakou-guest-list.csv" },
    { kind: "attendance", label: "Attendance report", file: "lakou-attendance-report.csv" },
    { kind: "vip", label: "VIP guest list", file: "lakou-vip-guests.csv" },
    { kind: "expenses", label: "Expense report", file: "lakou-expenses.csv" },
    { kind: "staff", label: "Staff activity report", file: "lakou-staff-activity.csv" },
];

export const ControlReports = () => {
    const { user } = useAuth();
    const [audit, setAudit] = useState(null);
    const [busy, setBusy] = useState("");

    const dl = async (path, file) => {
        setBusy(path);
        try {
            await downloadFile(path, file);
        } catch (err) {
            toast.error(apiError(err, "Download failed"));
        } finally {
            setBusy("");
        }
    };

    const loadAudit = async () => {
        try {
            const { data } = await api.get("/control/audit");
            setAudit(data.audit || []);
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="control-reports">
            <h1 className="font-display font-black text-xl">Reports & Exports</h1>

            <div className="space-y-2">
                {REPORTS.map((r) => (
                    <button key={r.kind} onClick={() => dl(`/control/reports/${r.kind}.csv`, r.file)} data-testid={`report-${r.kind}`}
                        className="w-full flex items-center justify-between rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 px-5 py-4 hover:border-lakou-gold/45 transition-colors">
                        <span className="text-sm text-lakou-cream">{r.label} <span className="font-mono2 text-[9px] text-lakou-sand uppercase">CSV</span></span>
                        <Download size={15} className={busy.includes(r.kind) ? "animate-bounce text-lakou-gold" : "text-lakou-gold"} />
                    </button>
                ))}
                <button onClick={() => dl("/control/reports/finance.pdf", "lakou-finance-report.pdf")} data-testid="report-finance-pdf"
                    className="w-full flex items-center justify-between rounded-2xl border border-lakou-gold/40 bg-lakou-gold/10 px-5 py-4 hover:bg-lakou-gold/15 transition-colors">
                    <span className="text-sm text-lakou-cream font-semibold">Financial report <span className="font-mono2 text-[9px] text-lakou-gold uppercase">PDF</span></span>
                    <Download size={15} className="text-lakou-gold" />
                </button>
            </div>

            {user?.role === "admin" && (
                <>
                    <button onClick={() => dl("/control/backup", `lakou-backup-${new Date().toISOString().slice(0, 10)}.json`)} data-testid="report-backup"
                        className="w-full flex items-center justify-between rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 px-5 py-4 hover:border-lakou-gold/45 transition-colors">
                        <span className="text-sm text-lakou-cream inline-flex items-center gap-2"><DatabaseBackup size={15} className="text-lakou-gold" /> Full database backup <span className="font-mono2 text-[9px] text-lakou-sand uppercase">JSON</span></span>
                        <Download size={15} className="text-lakou-gold" />
                    </button>

                    <section className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-gold flex items-center gap-2"><ScrollText size={12} /> Audit log</h2>
                            <button onClick={loadAudit} data-testid="audit-load" className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-lakou-gold">Load</button>
                        </div>
                        <div className="space-y-1.5" data-testid="audit-list">
                            {audit === null && <p className="text-xs text-lakou-sand">Tap Load to view recent admin activity.</p>}
                            {audit?.slice(0, 60).map((a) => (
                                <div key={a.id} className="flex items-center justify-between rounded-lg bg-lakou-raise/50 px-3.5 py-2">
                                    <span className="font-mono2 text-[10px] text-lakou-cream truncate">{a.action} <span className="text-lakou-sand">{a.detail}</span></span>
                                    <span className="font-mono2 text-[9px] text-lakou-sand shrink-0 ml-3">{a.user} · {a.at?.slice(5, 16).replace("T", " ")}</span>
                                </div>
                            ))}
                            {audit && !audit.length && <p className="text-xs text-lakou-sand">No activity recorded yet.</p>}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};
